import { useTranslator } from "components/Translator"
import { AdvertInteractionsProvider } from "domains/advert/AdvertInteractionsProvider"
import * as React from "react"
import backgroundImage from "img/background.jpg"
import * as SearchAdvertType from "enums/SearchAdvertType"
import * as SearchAdvertSort from "enums/SearchAdvertSort"
import * as UserScope from "enums/UserScope"
import { SearchPage } from "components/SearchPage"
import { AdvertResult } from "components/SearchResult"
import qs from "qs"
import partition from "lodash/partition"
import { PublicityConfigProvider } from "components/Publicity"
import { getPublicityConfigForAdvertType } from "components/Publicity/config"
import {
  today,
  getLocalTimeZone,
  toCalendarDateTime,
} from "@internationalized/date"

export function SearchAdvertIndexPage({
  adverts,
  pagination,
  formSearch,
  formSave,
  filters,
  popularSearches,
  savedSearches,
}) {
  const translator = useTranslator()

  const advertIds = React.useMemo(
    () => adverts.map((advert) => advert.id),
    [adverts],
  )

  const queryParams = qs.parse(window.location.search.slice(1)).a

  const entity = {
    type: SearchAdvertType.BOAT,
    nbResults: "10",
    sort: SearchAdvertSort.RELEVANCE,
    departurePortAddressRadius: 60,
    arrivalPortAddressRadius: 60,
    boatLength: {
      min: 1,
      max: 30,
    },
    userAge: {
      min: 18,
      max: 100,
    },
    salePrice: {
      min: 1,
      max: 3000000,
    },
    userScopes: [UserScope.USER, UserScope.ORG],
    ...queryParams,
  }

  const withMinDateFormSearch = addMinDate(formSearch)
  const formSearchLight = makeLightForm(withMinDateFormSearch)
  const enhancedFormSearch = enhanceFullForm(withMinDateFormSearch)

  const [inRadiusAdverts, outOfRadiusAdverts] = partition(adverts, (advert) => {
    if (!queryParams?.departurePortAddressRadius) {
      return true
    }

    const radius = queryParams.departurePortAddressRadius

    return advert.departureDistance <= radius
  })

  const advertToItem = (advert) => (
    <AdvertResult key={advert.id} advert={advert} />
  )

  const searchParams = new URLSearchParams(window.location.search)
  const currentPopularSearch =
    popularSearches.filter(
      (popularSearch) => popularSearch.id == (searchParams.get("sso") ?? null),
    )[0] ?? null

  return (
    <PublicityConfigProvider
      config={getPublicityConfigForAdvertType(searchParams.get("a[type]"))}
    >
      <AdvertInteractionsProvider ids={advertIds}>
        <SearchPage
          seoTitle={
            null !== currentPopularSearch
              ? currentPopularSearch.title
              : translator.trans(
                  "SearchPage.titleSEO.advert",
                  null,
                  "components",
                )
          }
          resultSuffix={translator.trans(
            "search.advert.index.resultSuffix",
            null,
            "pages",
          )}
          backgroundImage={backgroundImage}
          formSearch={enhancedFormSearch}
          formSearchLight={formSearchLight}
          formSave={formSave}
          filters={filters}
          entity={entity}
          pagination={pagination}
          mainItems={inRadiusAdverts.map(advertToItem)}
          secondaryItems={outOfRadiusAdverts.map(advertToItem)}
          panelsTitles={{
            boat: {
              1: translator.trans(
                "search.advert.index.panels.boat.1",
                null,
                "pages",
              ),
              2: translator.trans(
                "search.advert.index.panels.boat.2",
                null,
                "pages",
              ),
              3: translator.trans(
                "search.advert.index.panels.boat.3",
                null,
                "pages",
              ),
              4: translator.trans(
                "search.advert.index.panels.boat.4",
                null,
                "pages",
              ),
              5: translator.trans(
                "search.advert.index.panels.boat.5",
                null,
                "pages",
              ),
              6: translator.trans(
                "search.advert.index.panels.boat.6",
                null,
                "pages",
              ),
            },
            crew: {
              1: translator.trans(
                "search.advert.index.panels.crew.1",
                null,
                "pages",
              ),
              2: translator.trans(
                "search.advert.index.panels.crew.2",
                null,
                "pages",
              ),
              3: translator.trans(
                "search.advert.index.panels.crew.3",
                null,
                "pages",
              ),
              4: translator.trans(
                "search.advert.index.panels.crew.4",
                null,
                "pages",
              ),
              5: translator.trans(
                "search.advert.index.panels.crew.5",
                null,
                "pages",
              ),
            },
            fleet: {
              1: translator.trans(
                "search.advert.index.panels.fleet.1",
                null,
                "pages",
              ),
              2: translator.trans(
                "search.advert.index.panels.fleet.2",
                null,
                "pages",
              ),
              3: translator.trans(
                "search.advert.index.panels.fleet.3",
                null,
                "pages",
              ),
              4: translator.trans(
                "search.advert.index.panels.fleet.4",
                null,
                "pages",
              ),
            },
            sale: {
              1: translator.trans(
                "search.advert.index.panels.sale.1",
                null,
                "pages",
              ),
              2: translator.trans(
                "search.advert.index.panels.sale.2",
                null,
                "pages",
              ),
              3: translator.trans(
                "search.advert.index.panels.sale.3",
                null,
                "pages",
              ),
              4: translator.trans(
                "search.advert.index.panels.sale.4",
                null,
                "pages",
              ),
              5: translator.trans(
                "search.advert.index.panels.sale.5",
                null,
                "pages",
              ),
              6: translator.trans(
                "search.advert.index.panels.sale.6",
                null,
                "pages",
              ),
              7: translator.trans(
                "search.advert.index.panels.sale.7",
                null,
                "pages",
              ),
              8: translator.trans(
                "search.advert.index.panels.sale.8",
                null,
                "pages",
              ),
              9: translator.trans(
                "search.advert.index.panels.sale.9",
                null,
                "pages",
              ),
            },
            rental: {
              1: translator.trans(
                "search.advert.index.panels.rental.1",
                null,
                "pages",
              ),
              2: translator.trans(
                "search.advert.index.panels.rental.2",
                null,
                "pages",
              ),
              3: translator.trans(
                "search.advert.index.panels.rental.3",
                null,
                "pages",
              ),
              4: translator.trans(
                "search.advert.index.panels.rental.4",
                null,
                "pages",
              ),
              5: translator.trans(
                "search.advert.index.panels.rental.5",
                null,
                "pages",
              ),
              6: translator.trans(
                "search.advert.index.panels.rental.6",
                null,
                "pages",
              ),
              7: translator.trans(
                "search.advert.index.panels.rental.7",
                null,
                "pages",
              ),
              8: translator.trans(
                "search.advert.index.panels.rental.8",
                null,
                "pages",
              ),
            },
          }}
          popularSearches={popularSearches}
          savedSearches={savedSearches}
        />
      </AdvertInteractionsProvider>
    </PublicityConfigProvider>
  )
}

const makeLightForm = (originalForm) => {
  const lightForm = structuredClone(originalForm)

  lightForm.options.widget = "advert_filter_light"
  lightForm.options.layout = "inline"

  lightForm.properties = {
    type: lightForm.properties.type,
    boatModel: lightForm.properties.boatModel,
    departurePortAddress: lightForm.properties.departurePortAddress,
    departureDate: lightForm.properties.departureDate,
  }

  lightForm.properties.type.options.choice.expanded = false

  lightForm.properties.boatModel.options.dependencies = [
    {
      property: "type",
      mode: "equal",
      value: [SearchAdvertType.SALE, SearchAdvertType.RENTAL],
    },
  ]

  lightForm.properties.departureDate.options.dependencies = [
    {
      property: "type",
      mode: "equal",
      value: [
        SearchAdvertType.BOAT,
        SearchAdvertType.CREW,
        SearchAdvertType.FLEET,
      ],
    },
  ]

  lightForm.properties.departurePortAddress.options.dependencies = [
    {
      property: "type",
      mode: "equal",
      value: [
        SearchAdvertType.BOAT,
        SearchAdvertType.CREW,
        SearchAdvertType.FLEET,
        SearchAdvertType.RENTAL,
      ],
    },
  ]

  return lightForm
}

const enhanceFullForm = (originalForm) => {
  const enhancedForm = structuredClone(originalForm)

  if (enhancedForm.properties.type) {
    enhancedForm.properties.type.options.choice.expanded = true
  }

  return enhancedForm
}

function addMinDate(form) {
  const enhancedForm = structuredClone(form)

  const min =
    toCalendarDateTime(today(getLocalTimeZone()))
      .set({
        hour: 0,
        minute: 0,
        second: 0,
        millisecond: 0,
      })
      .toString() + "Z"

  if (enhancedForm.properties.departureDate) {
    enhancedForm.properties.departureDate.options.attr = {
      ...enhancedForm.properties.departureDate.options.attr,
      min,
    }
  }

  return enhancedForm
}
